// FormulaTransformOpenTask.js
// Otwarte zadanie: wyznaczanie wskazanej wielkosci ze wzoru.

import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

export const FORMULA_TEMPLATES = [
  {
    id: "linear_shift_divide",
    level: 1,
    formula: "y=bx+a",
    solveVar: "x",
    answer: "\\frac{y-a}{b}",
    accepted: ["(y-a)/b", "x=(y-a)/b"],
    steps: [
      "y=bx+a",
      "y-a=bx",
      "x=\\frac{y-a}{b}",
    ],
    note: "Najpierw odejmujemy a od obu stron, potem dzielimy przez b.",
  },
  {
    id: "triangle_area_height",
    level: 1,
    formula: "P=\\frac{ah}{2}",
    solveVar: "h",
    answer: "\\frac{2P}{a}",
    accepted: ["2P/a", "(2P)/a", "2*P/a"],
    steps: [
      "P=\\frac{ah}{2}",
      "2P=ah",
      "h=\\frac{2P}{a}",
    ],
    note: "Usuwamy mianownik 2 przez pomnozenie obu stron przez 2.",
  },
  {
    id: "work_force_distance",
    level: 1,
    formula: "W=Fs",
    solveVar: "F",
    answer: "\\frac{W}{s}",
    accepted: ["W/s"],
    steps: [
      "W=Fs",
      "F=\\frac{W}{s}",
    ],
    note: "Gdy szukana wielkosc jest mnozona przez inna, dzielimy przez ten czynnik.",
  },
  {
    id: "energy_height",
    level: 2,
    formula: "E=mgh",
    solveVar: "h",
    answer: "\\frac{E}{mg}",
    accepted: ["E/(mg)", "E/mg", "E/(m*g)"],
    steps: [
      "E=mgh",
      "h=\\frac{E}{mg}",
    ],
    note: "Iloczyn mg traktujemy jak jeden czynnik stojacy przy h.",
  },
  {
    id: "resistance_fraction",
    level: 2,
    formula: "R=\\frac{ab}{x}",
    solveVar: "x",
    answer: "\\frac{ab}{R}",
    accepted: ["ab/R", "a*b/R", "(ab)/R", "(a*b)/R"],
    steps: [
      "R=\\frac{ab}{x}",
      "Rx=ab",
      "x=\\frac{ab}{R}",
    ],
    note: "Najpierw mnozymy obie strony przez x, a potem dzielimy przez R.",
  },
  {
    id: "circumradius_formula",
    level: 2,
    formula: "P=\\frac{abc}{4R}",
    solveVar: "R",
    answer: "\\frac{abc}{4P}",
    accepted: ["abc/(4P)", "a*b*c/(4P)", "(abc)/(4P)", "(a*b*c)/(4P)"],
    steps: [
      "P=\\frac{abc}{4R}",
      "4PR=abc",
      "R=\\frac{abc}{4P}",
    ],
    note: "To wzor z geometrii: wyznaczamy R z ulamka, wiec najpierw usuwamy mianownik.",
  },
  {
    id: "bracket_simple",
    level: 3,
    formula: "V=5(a+2k)",
    solveVar: "k",
    answer: "\\frac{V-5a}{10}",
    accepted: ["(V-5a)/10", "(V-5*a)/10"],
    steps: [
      "V=5(a+2k)",
      "V=5a+10k",
      "V-5a=10k",
      "k=\\frac{V-5a}{10}",
    ],
    note: "Najpierw roznosimy 5 na nawias, bo k jest ukryte w nawiasie.",
  },
  {
    id: "collect_factor",
    level: 3,
    formula: "k(a+b)-1=c",
    solveVar: "k",
    answer: "\\frac{c+1}{a+b}",
    accepted: ["(c+1)/(a+b)"],
    steps: [
      "k(a+b)-1=c",
      "k(a+b)=c+1",
      "k=\\frac{c+1}{a+b}",
    ],
    note: "Caly nawias a+b jest czynnikiem stojacym przy k.",
  },
  {
    id: "two_places",
    level: 3,
    formula: "T+p=3(a^2+2ak)",
    solveVar: "k",
    answer: "\\frac{T+p-3a^2}{6a}",
    accepted: ["(T+p-3a^2)/(6a)", "(T+p-3*a^2)/(6a)"],
    steps: [
      "T+p=3(a^2+2ak)",
      "T+p=3a^2+6ak",
      "T+p-3a^2=6ak",
      "k=\\frac{T+p-3a^2}{6a}",
    ],
    note: "Po rozwinieciu nawiasu przenosimy skladnik bez k na druga strone.",
  },
  {
    id: "nested_negative",
    level: 3,
    formula: "A-2b=p(3-pk)",
    solveVar: "k",
    answer: "\\frac{3p-A+2b}{p^2}",
    accepted: ["(3p-A+2b)/p^2", "(3*p-A+2*b)/p^2", "(2b+3p-A)/p^2"],
    steps: [
      "A-2b=p(3-pk)",
      "A-2b=3p-p^2k",
      "p^2k=3p-A+2b",
      "k=\\frac{3p-A+2b}{p^2}",
    ],
    note: "Tu najlatwiej przeniesc skladniki tak, aby p^2k zostalo dodatnie.",
  },
];

function normalize(raw) {
  let s = String(raw ?? "")
    .replace(/−/g, "-")
    .replace(/\\cdot/g, "*")
    .replace(/\\dfrac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)")
    .replace(/\s+/g, "")
    .toLowerCase();

  s = s.replace(/^[a-z]=/, "");
  s = s.replace(/\*/g, "");
  s = s.replace(/\(([^()+\-*/^]+)\)/g, "$1");
  return s;
}

function taskFromTemplate(template) {
  return {
    type: "FormulaTransformOpenTask",
    data: template,
    source: { variant: "random", skill: "formula_transform" },
    tags: ["wzory", "przeksztalcanie_wzorow", `poziom_${template.level}`],
  };
}

export function generateTask(options = {}) {
  const candidates = options.level
    ? FORMULA_TEMPLATES.filter((template) => template.level === options.level)
    : FORMULA_TEMPLATES;
  return taskFromTemplate(pick(candidates));
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Wyznacz ze wzoru wskazana wielkosc:{" "}
        <InlineMath math={data.solveVar} />.
      </p>
      <BlockMath math={data.formula} />
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder={`np. ${data.solveVar}=...`}
      showPreview
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const user = normalize(userAnswer);
  const accepted = [data.answer, ...data.accepted].map(normalize);

  return {
    isCorrect: accepted.includes(user),
    expected: `${data.solveVar}=${data.answer}`,
  };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.solveVar}=${data.answer}`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const raw = String(userAnswer);
  const latex = raw.includes("=") ? raw : `${data.solveVar}=${raw}`;
  return <InlineMath math={latex.replace(/\*/g, "\\cdot ")} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Poziom {data.level}.</strong> {data.note}
      </p>
      {data.steps.map((step, index) => (
        <React.Fragment key={step}>
          <p>
            <strong>Krok {index + 1}.</strong>{" "}
            {index === data.steps.length - 1
              ? "Otrzymujemy szukana wielkosc."
              : "Wykonujemy rownowazne przeksztalcenie wzoru."}
          </p>
          <BlockMath math={step} />
        </React.Fragment>
      ))}
    </div>
  );
}
